// The Mood tab: employer satisfaction. The meter shows the room's mood, and
// three faces rate the employer, once a minute. Only the llamas see this.
// The faces are three 32px cells of one sheet, public/mood/llama-happiness.png:
// sad, meh and happy, left to right, each on a light key. CSS picks the cell
// (.face-art).

import type { Mood } from '../../protocol';
import { KEY_INK, keycap, setKeyEnabled, setKeyHeld } from '../chrome';
import { buzz, el } from '../dom';
import type { Page } from '../page';
import { pixelText } from '../pixelText';
import { say } from '../say';
import { voteWait } from '../state';
import { STRINGS } from '../strings';

const TITLE_SCALE = 2;
const VOTE_BUZZ_MS = 20;
const LIGHT = '#ece8ff';

const FACES: { value: Mood; art: keyof typeof STRINGS.moods }[] = [
  { value: -1, art: 'sad' },
  { value: 0, art: 'meh' },
  { value: 1, art: 'happy' },
];

export function buildMood(page: Page): HTMLElement {
  const state = page.state();
  const box = el('section', 'tab-mood');
  page.meter.setMood(state.mood.mood);
  const gauge = el('div', 'gauge');
  // The meter's name as a small label, so the faces fit on a short glass.
  gauge.append(
    say(STRINGS.satisfaction, 'label'),
    page.meter.canvas,
    say(
      state.mood.votes === 0 ? STRINGS.moodNone : STRINGS.moodVotes(state.mood.votes),
      'note',
      'gauge-votes',
    ),
  );
  const faces = el('div', 'faces');
  for (const face of FACES) faces.append(faceKey(page, face.value, face.art));
  box.append(gauge, say(STRINGS.moodAsk, 'line', 'mood-ask'), faces, el('div', 'vote-wait'));
  return box;
}

// A face on a light key: the llama's mood art and its word. The word comes in
// two inks: dark for the light key, lit for the face this llama picked.
function faceKey(page: Page, value: Mood, art: keyof typeof STRINGS.moods): HTMLButtonElement {
  const face = el('span', 'mood-face');
  face.append(
    el('span', `face-art face-art-${art}`),
    pixelText(
      STRINGS.moods[art].toLowerCase(),
      'monogram',
      TITLE_SCALE,
      KEY_INK.light,
      'face-word',
    ),
    pixelText(STRINGS.moods[art].toLowerCase(), 'monogram', TITLE_SCALE, LIGHT, 'face-word-lit'),
  );
  const key = keycap({
    label: STRINGS.moods[art],
    content: face,
    className: 'key-face',
    onPress: () => {
      if (page.state().seat === null || voteWait(page.state(), Date.now()) > 0) return;
      buzz(VOTE_BUZZ_MS);
      page.send({ t: 'mood', value });
    },
  });
  key.dataset.value = String(value);
  return key;
}

// Called every tick: the faces dim while the vote cools down, and the wait counts.
export function updateMood(page: Page, root: HTMLElement): void {
  const state = page.state();
  const wait = voteWait(state, Date.now());
  // Only a seated llama rates. One in the queue sees the meter, not the faces.
  const seated = state.seat !== null;
  for (const face of root.querySelectorAll<HTMLButtonElement>('.key-face')) {
    setKeyEnabled(face, seated && wait <= 0);
    setKeyHeld(face, seated && wait > 0 && face.dataset.value === String(state.myVote));
  }
  const line = root.querySelector<HTMLElement>('.vote-wait');
  if (!line) return;
  const text = !seated ? STRINGS.moodQueued : wait > 0 ? STRINGS.moodNext(wait) : '';
  if (line.dataset.text === text) return;
  line.dataset.text = text;
  line.replaceChildren(...(text ? [say(text, 'note')] : []));
}
